import { parseFragment, serialize } from 'parse5';

const BLOCK_BREAK_PATTERN = /<\/?(?:address|article|aside|blockquote|div|figcaption|figure|footer|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)[^>]*>/gi;

type HtmlNode = {
    nodeName: string;
    tagName?: string;
    attrs?: Array<{ name: string; value: string }>;
    childNodes?: HtmlNode[];
};

const RICH_HTML_TAGS = new Set([
    'a', 'article', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup', 'dd', 'del', 'div', 'dl', 'dt',
    'em', 'figcaption', 'figure', 'font', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
    'pre', 's', 'section', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead',
    'tr', 'u', 'ul',
]);

const DROP_RICH_HTML_TAGS = new Set([
    'applet', 'audio', 'base', 'button', 'embed', 'form', 'iframe', 'input', 'link', 'meta', 'object', 'script',
    'select', 'source', 'style', 'svg', 'textarea', 'video', 'xml',
]);

const RICH_HTML_ATTRIBUTES = new Set([
    'alt', 'align', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'class', 'color', 'colspan', 'colwidth', 'dir', 'face', 'height', 'href',
    'data-columns', 'data-contract-table', 'data-contract-table-label', 'data-editor-column', 'data-editor-columns', 'id', 'lang', 'rel', 'rowspan', 'scope', 'size', 'src',
    'start', 'style', 'target', 'title', 'width',
]);

const RICH_HTML_STYLE_PROPERTIES = new Set([
    'background', 'background-color', 'border', 'border-bottom', 'border-bottom-color', 'border-bottom-style', 'border-bottom-width',
    'border-collapse', 'border-color', 'border-left', 'border-left-color', 'border-left-style', 'border-left-width',
    'border-right', 'border-right-color', 'border-right-style', 'border-right-width', 'border-spacing', 'border-style',
    'border-top', 'border-top-color', 'border-top-style', 'border-top-width', 'border-width', 'color', 'font-family',
    'font-size', 'font-style', 'font-variant', 'font-weight', 'letter-spacing', 'line-height', 'list-style-type', 'margin', 'margin-bottom',
    'margin-left', 'margin-right', 'margin-top', 'max-width', 'min-width', 'padding', 'padding-bottom', 'padding-left',
    'padding-right', 'padding-top', 'text-align', 'text-decoration', 'text-indent', 'text-transform', 'vertical-align',
    'white-space', 'width',
]);

function safeRichTextUrl(value: string, allowMailto: boolean): boolean {
    const normalized = value.trim().replace(/[\u0000-\u0020\u007f]+/g, '').toLowerCase();
    if (!normalized) return false;
    if (/^(https?:|\/\/|\/|\.\.?\/|#)/.test(normalized)) return true;
    return allowMailto && normalized.startsWith('mailto:');
}

function safeRichTextStyle(value: string): string {
    return value.split(';').flatMap((declaration) => {
        const separator = declaration.indexOf(':');
        if (separator < 1) return [];
        const property = declaration.slice(0, separator).trim().toLowerCase();
        const styleValue = declaration.slice(separator + 1).trim();
        if (!RICH_HTML_STYLE_PROPERTIES.has(property) || !styleValue || styleValue.length > 160) return [];
        if (/[<>\\{}]/.test(styleValue) || /url\s*\(|expression\s*\(|javascript:|vbscript:|@import|behavior\s*:|-moz-binding/i.test(styleValue)) return [];
        return [`${property}: ${styleValue}`];
    }).join('; ');
}

function sanitizeRichHtmlChildren(parent: HtmlNode) {
    if (!parent.childNodes) return;
    parent.childNodes = parent.childNodes.flatMap((node) => {
        if (node.nodeName === '#comment') return [];
        if (!node.tagName) return [node];

        const tagName = node.tagName.toLowerCase();
        if (DROP_RICH_HTML_TAGS.has(tagName)) return [];

        sanitizeRichHtmlChildren(node);
        if (!RICH_HTML_TAGS.has(tagName)) return node.childNodes || [];

        node.attrs = (node.attrs || []).flatMap((attr) => {
            const name = attr.name.toLowerCase();
            const value = attr.value.trim();
            if (!RICH_HTML_ATTRIBUTES.has(name)) return [];
            if (name === 'style') {
                const style = safeRichTextStyle(value);
                return style ? [{ name, value: style }] : [];
            }
            if (name === 'class') return value.length <= 1024 ? [{ name, value }] : [];
            if (name === 'href' && tagName === 'a') return safeRichTextUrl(value, true) ? [{ name, value }] : [];
            if (name === 'src' && tagName === 'img') return safeRichTextUrl(value, false) ? [{ name, value }] : [];
            if (name === 'id') return /^[a-z][a-z0-9_.:-]{0,127}$/i.test(value) ? [{ name, value }] : [];
            if (name === 'dir') return /^(ltr|rtl|auto)$/i.test(value) ? [{ name, value }] : [];
            if (name === 'target') return value === '_blank' && tagName === 'a' ? [{ name, value }] : [];
            return [{ name, value }];
        });

        if (tagName === 'a' && node.attrs?.some(attr => attr.name === 'target' && attr.value === '_blank')) {
            node.attrs = [...node.attrs.filter(attr => attr.name !== 'rel'), { name: 'rel', value: 'noopener noreferrer nofollow' }];
        }
        return [node];
    });
}

/** Keep document formatting while stripping executable markup from rich HTML previews. */
export function sanitizeRichTextHtml(value?: string | null): string {
    const fragment = parseFragment(String(value || ''));
    sanitizeRichHtmlChildren(fragment as unknown as HtmlNode);
    return serialize(fragment);
}

function decodeHtmlEntities(value: string): string {
    const namedEntities: Record<string, string> = {
        amp: '&',
        apos: '\'',
        gt: '>',
        lt: '<',
        nbsp: ' ',
        quot: '"',
    };

    return value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, code: string) => {
        const normalized = code.toLowerCase();
        if (normalized.startsWith('#x')) {
            const value = Number.parseInt(normalized.slice(2), 16);
            return Number.isFinite(value) ? String.fromCodePoint(value) : entity;
        }
        if (normalized.startsWith('#')) {
            const value = Number.parseInt(normalized.slice(1), 10);
            return Number.isFinite(value) ? String.fromCodePoint(value) : entity;
        }
        return namedEntities[normalized] ?? entity;
    });
}

export function richTextToPlainText(value?: string | null): string {
    return decodeHtmlEntities(
        String(value || '')
            .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(BLOCK_BREAK_PATTERN, '\n')
            .replace(/<[^>]+>/g, ' '),
    )
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
}

export function hasRichTextContent(value?: string | null): boolean {
    return richTextToPlainText(value).length > 0 || /<(?:img|table|hr)\b/i.test(String(value || ''));
}
